/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Eye as EyeIcon,
} from 'react-feather';
import { useSnackbar } from 'notistack';
import Request from '../../../requests/api/performance_cycle_instances';
import { DEVELOPER, SUPER_ADMIN } from '../../../utils/const/user_types';

const sortOptions = [
  {
    value: 'updated_at|desc',
    label: 'Más nuevo primero',
  },
  {
    value: 'updated_at|asc',
    label: 'Más antiguo primero',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {},
  queryField: {
    width: 500,
  },
  bulkOperations: {
    position: 'relative',
  },
  bulkActions: {
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: 6,
    position: 'absolute',
    width: '100%',
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
  },
  bulkAction: {
    marginLeft: theme.spacing(2),
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
  },
}));

function Results({
  className,
  performanceCycleInstances,
  pageOfTable,
  setPageOfTable,
  limit,
  setLimit,
  searchValue,
  setSearchValue,
  totalPerformanceCycleInstances,
  ...rest
}) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [selectedPerformanceCycleInstances, setSelectedPerformanceCycleInstances] = useState([]);
  const [sort, setSort] = useState(sortOptions[0].value);
  const [currentPerformanceCycleInstances, setCurrentPerformanceCycleInstances] = useState(performanceCycleInstances);

  useEffect(() => {
    setCurrentPerformanceCycleInstances(performanceCycleInstances);
  }, [performanceCycleInstances]);

  const handleQueryChange = (event) => {
    event.persist();
    setSearchValue(event.target.value);
  };

  const handleSortChange = (event) => {
    event.persist();
    setSort(event.target.value);
  };

  const handleSelectAllPerformanceCycleInstances = (event) => {
    setSelectedPerformanceCycleInstances(event.target.checked
      ? currentPerformanceCycleInstances.map((performanceCycleInstance) => performanceCycleInstance.id)
      : []);
  };

  const handleSelectOnePerformanceCycleInstance = (event, performanceCycleInstanceId) => {
    if (!selectedPerformanceCycleInstances.includes(performanceCycleInstanceId)) {
      setSelectedPerformanceCycleInstances((prevSelected) => [...prevSelected, performanceCycleInstanceId]);
    } else {
      setSelectedPerformanceCycleInstances((prevSelected) => prevSelected.filter((id) => id !== performanceCycleInstanceId));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPageOfTable(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  // Usually query is done on backend with indexing solutions
  const enableBulkOperations = selectedPerformanceCycleInstances.length > 0;
  const selectedSomePerformanceCycleInstances = selectedPerformanceCycleInstances.length > 0 && selectedPerformanceCycleInstances.length < currentPerformanceCycleInstances.length;
  const selectedAllPerformanceCycleInstances = selectedPerformanceCycleInstances.length === currentPerformanceCycleInstances.length;
  const account = useSelector((state) => state.account);

  const deletePerformanceCycleInstances = async () => {
    if (account.user.role.name !== SUPER_ADMIN) {
      enqueueSnackbar('Debe ser Administrador!', {
        variant: 'error',
      });
    } else if (selectedPerformanceCycleInstances.includes(account.user.id)) {
      enqueueSnackbar('No puede eliminar su propia cuenta', {
        variant: 'error',
      });
    } else {
      for (let a = 0; a < selectedPerformanceCycleInstances.length; a += 1) {
        Request.deletePerformanceCycleInstance(selectedPerformanceCycleInstances[a]);
        enqueueSnackbar('Instancias eliminadas!', {
          variant: 'success',
        });
      }
      setCurrentPerformanceCycleInstances(currentPerformanceCycleInstances.filter((e) => (!selectedPerformanceCycleInstances.includes(e.id))));
      setSelectedPerformanceCycleInstances([]);
    }
  };

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Divider />
      <Box
        p={2}
        minHeight={56}
        display="flex"
        alignItems="center"
      >
        <TextField
          className={classes.queryField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon
                  fontSize="small"
                  color="action"
                >
                  <SearchIcon />
                </SvgIcon>
              </InputAdornment>
            ),
          }}
          onChange={handleQueryChange}
          placeholder="Buscar instancia"
          value={searchValue}
          variant="outlined"
        />
        <Box flexGrow={1} />
        <TextField
          label="Orden"
          name="sort"
          onChange={handleSortChange}
          select
          SelectProps={{ native: true }}
          value={sort}
          variant="outlined"
        >
          {sortOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </TextField>
      </Box>
      {enableBulkOperations && (
        <div className={classes.bulkOperations}>
          <div className={classes.bulkActions}>
            <Checkbox
              checked={selectedAllPerformanceCycleInstances}
              indeterminate={selectedSomePerformanceCycleInstances}
              onChange={handleSelectAllPerformanceCycleInstances}
            />
            <Button
              variant="outlined"
              className={classes.bulkAction}
              onClick={() => deletePerformanceCycleInstances()}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
      <PerfectScrollbar>
        <Box minWidth={700}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedAllPerformanceCycleInstances}
                    indeterminate={selectedSomePerformanceCycleInstances}
                    onChange={handleSelectAllPerformanceCycleInstances}
                  />
                </TableCell>
                <TableCell>
                  Ciclo de performance
                </TableCell>
                <TableCell>
                  Nombre completo
                </TableCell>
                <TableCell>
                  Fecha inicio
                </TableCell>
                <TableCell>
                  Fecha término
                </TableCell>
                <TableCell>
                  Evaluación proyectos
                </TableCell>
                <TableCell>
                  Evaluación objetivos
                </TableCell>
                <TableCell>
                  Evaluación horas
                </TableCell>
                <TableCell align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentPerformanceCycleInstances.map((performanceCycleInstance) => {
                const isPerformanceCycleInstanceSelected = selectedPerformanceCycleInstances.includes(performanceCycleInstance.id);

                return (
                  <TableRow
                    hover
                    key={performanceCycleInstance.id}
                    selected={isPerformanceCycleInstanceSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isPerformanceCycleInstanceSelected}
                        onChange={(event) => handleSelectOnePerformanceCycleInstance(event, performanceCycleInstance.id)}
                        value={isPerformanceCycleInstanceSelected}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {performanceCycleInstance.performance_cycle.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {`${performanceCycleInstance.collaborator.user.name} ${performanceCycleInstance.collaborator.user.lastname}`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {performanceCycleInstance.performance_cycle.startDate.slice(0, 10).split('-').reverse().join('-')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {performanceCycleInstance.performance_cycle.endDate.slice(0, 10).split('-').reverse().join('-')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {performanceCycleInstance.projectsEvaluationAvg || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {performanceCycleInstance.objectivesEvaluationAvg || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {performanceCycleInstance.hoursEvaluation || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {account.user.role.name !== DEVELOPER ? (
                        <IconButton
                          component={RouterLink}
                          to={`/administracion/performance-cycle-instances/${performanceCycleInstance.id}/editar`}
                        >
                          <SvgIcon fontSize="small">
                            <EditIcon />
                          </SvgIcon>
                        </IconButton>) : null}
                      <IconButton
                        component={RouterLink}
                        to={`/administracion/performance-cycle-instances/${performanceCycleInstance.id}`}
                      >
                        <SvgIcon fontSize="small">
                          <EyeIcon />
                        </SvgIcon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={totalPerformanceCycleInstances}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={pageOfTable}
        rowsPerPage={limit}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Filas por página"
      />
    </Card>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  performanceCycleInstances: PropTypes.array,
  pageOfTable: PropTypes.number,
  setPageOfTable: PropTypes.func,
  limit: PropTypes.number,
  setLimit: PropTypes.func,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  totalPerformanceCycleInstances: PropTypes.number,
};

Results.defaultProps = {
  performanceCycleInstances: [],
};

export default Results;
